import { useEffect, useState } from 'react';
import FloatingCard from '../../../components/general/FloatingCard';
import SetMissionNoteDialog from '../../../components/dialogs/SetMissionNoteDialog';
import SubmitDiscrepancyReportDialog from '../../../components/dialogs/SubmitDiscrepancyReportDialog';
import VehicleModuleVersionsDialog from '../../../components/dialogs/VehicleModuleVersionsDialog';
import { InfoRow } from '../../../components/general/InfoRow';
import { Spinner } from '../../../components/general/Spinner';
import { Text } from '../../../components/general/Text';
import { en } from '../../../locales/en';
import { useLocalizedErrorMessage } from '../../../hooks/useLocalizedErrorMessage';
import { useDialogQueueStore } from '../../../store/useDialogQueueStore';
import { useGcsStore } from '../../../store/useGcsStore';
import { DeliveryOutcome } from '../../../types/gcs/postflight';
import type { DeliveryOutcome as DeliveryOutcomeType, PostflightMissionSummary } from '../../../types/gcs/postflight';
import type { VehicleId } from '../../../types/gcs/vehicle';
import type { MenuItemTileProps } from '../../../components/general/MenuItemTile';
import { AsyncAction } from '../../../types/general/asyncAction';
import { formatPercentage, getUnitFormatters } from '../../../utils/formatters';
import { selectUnitSystem } from '../../../store/selectors/selectUnitSystem';
import VehiclePanelHeader from './VehiclePanelHeader';
import { McErrorTile } from './McErrorTile';
import MissionLogsDialog from '../../../components/dialogs/MissionLogsDialog';
import { MissionStepper } from './MissionStepper';

interface PostflightVehiclePanelProps {
  vehicleId: VehicleId;
  gcsId: string;
  onClose: () => void;
  onFocusVehicle: () => void;
}

function PostflightVehiclePanel({ vehicleId, gcsId, onClose, onFocusVehicle }: PostflightVehiclePanelProps) {
  const [postflightSummary, setPostflightSummary] = useState<AsyncAction<PostflightMissionSummary>>(
    AsyncAction.initial()
  );
  const unitSystem = useGcsStore((state) => selectUnitSystem(state.data, gcsId));
  const unitFormatters = getUnitFormatters(unitSystem);
  const getLocalizedErrorMessage = useLocalizedErrorMessage();
  const fetchPostflightMissionSummary = useGcsStore((state) => state.fetchPostflightMissionSummary);
  const { showDialog, hideDialog } = useDialogQueueStore();

  const liveMission = useGcsStore((state) => {
    const gcsData = state.data.get(gcsId);
    if (!gcsData) return null;
    return gcsData.liveMissionsMap.get(vehicleId);
  });

  useEffect(() => {
    if (!liveMission?.missionId) return;

    const fetchData = async () => {
      setPostflightSummary(AsyncAction.loading());
      try {
        const data = await fetchPostflightMissionSummary(gcsId, liveMission.missionId);
        setPostflightSummary(AsyncAction.success(data));
      } catch (error) {
        setPostflightSummary(AsyncAction.failure(error));
      }
    };

    fetchData();
  }, [liveMission?.missionId, gcsId, fetchPostflightMissionSummary]);

  const getDeliveryOutcomeLabel = (outcome: DeliveryOutcomeType): string => {
    switch (outcome) {
      case DeliveryOutcome.DELIVERED:
        return en.postflightPanel.delivered;
      case DeliveryOutcome.CANCELED:
        return en.postflightPanel.canceled;
      case DeliveryOutcome.ABORTED:
        return en.postflightPanel.aborted;
      case DeliveryOutcome.OTHER:
        return en.postflightPanel.other;
      default:
        return outcome;
    }
  };

  const menuItems: MenuItemTileProps[] = [
    {
      label: en.vehiclePanelMenuItems.showVersions,
      onClick: () => {
        showDialog({
          id: `vehicle-module-versions-${vehicleId}`,
          component: <VehicleModuleVersionsDialog vehicleId={vehicleId} onClose={hideDialog} />
        });
      }
    },
    {
      label: en.vehiclePanelMenuItems.missionLogs,
      onClick: () => {
        if (!liveMission) return;
        showDialog({
          id: `mission-logs-dialog-${liveMission.missionId}`,
          component: (
            <MissionLogsDialog
              gcsId={gcsId}
              vehicleId={vehicleId}
              missionId={liveMission.missionId}
              onClose={hideDialog}
            />
          )
        });
      }
    },
    {
      label: en.vehiclePanelMenuItems.missionNote,
      onClick: () => {
        if (!liveMission) return;
        showDialog({
          id: 'set-mission-note',
          component: (
            <SetMissionNoteDialog
              gcsId={gcsId}
              vehicleId={vehicleId}
              missionId={liveMission.missionId}
              onClose={hideDialog}
            />
          )
        });
      }
    },
    {
      label: en.vehiclePanelMenuItems.discrepancyReport,
      onClick: () => {
        showDialog({
          id: 'submit-discrepancy-report',
          component: <SubmitDiscrepancyReportDialog gcsId={gcsId} vehicleId={vehicleId} onClose={hideDialog} />
        });
      }
    }
  ];

  return (
    <FloatingCard>
      <VehiclePanelHeader
        vehicleId={vehicleId}
        gcsId={gcsId}
        onFocusVehicle={onFocusVehicle}
        onClose={onClose}
        menuItems={menuItems}
      />
      <McErrorTile gcsId={gcsId} vehicleId={vehicleId} />

      {/* Scrollable Content */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          flex: 1,
          overflowY: 'auto'
        }}
      >
        <MissionStepper gcsId={gcsId} vehicleId={vehicleId} />
        {/* Loading State */}
        {postflightSummary.isLoading && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '32px'
            }}
          >
            <Spinner />
          </div>
        )}

        {/* Error State */}
        {postflightSummary.isFailure && (
          <Text variant='body2' style={{ color: 'var(--color-error)', textAlign: 'center' }}>
            {getLocalizedErrorMessage(postflightSummary.error)}
          </Text>
        )}

        {/* Success State - Mission Summary Card */}
        {postflightSummary.isSuccess && postflightSummary.data && (
          <>
            <div
              style={{
                border: 'var(--card-border-stroke) var(--card-border-style) var(--card-border-color)',
                borderRadius: 'var(--card-border-radius)',
                padding: '16px',
                backgroundColor: '#ffffff',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}
            >
              <InfoRow
                label={en.postflightPanel.distanceTravelled}
                value={unitFormatters.formatDistance(postflightSummary.data.distanceTravelledM)}
              />
              <InfoRow
                label={en.postflightPanel.batteryConsumed}
                value={formatPercentage(postflightSummary.data.batteryConsumedPct)}
              />
            </div>
            <div
              style={{
                border: 'var(--card-border-stroke) var(--card-border-style) var(--card-border-color)',
                borderRadius: 'var(--card-border-radius)',
                padding: '16px',
                backgroundColor: '#ffffff',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}
            >
              <InfoRow
                label={en.postflightPanel.packageWeight}
                value={
                  postflightSummary.data.packageWeightKg != null
                    ? unitFormatters.formatWeight(postflightSummary.data.packageWeightKg)
                    : en.common.notAvailable
                }
              />
              <InfoRow label={en.postflightPanel.order} value={postflightSummary.data.orderName} />
              <InfoRow
                label={en.postflightPanel.deliveryOutcome}
                value={getDeliveryOutcomeLabel(postflightSummary.data.deliveryOutcome)}
              />
            </div>
          </>
        )}
      </div>
    </FloatingCard>
  );
}

export default PostflightVehiclePanel;
